import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { createContext, ReactNode, useContext, useRef } from 'react';
import { useAuth } from './AuthContext';
import { MutationOptions } from '../services/general.types';
import { useIntegrationService } from '../services/integration.service';
import { Integration } from '../services/integration.types';
import { QUERY_KEYS } from '../utils/queryKeys';

const areArraysDifferent = (arr1: string[], arr2: string[]): boolean => {
    if (arr1.length !== arr2.length) return true;
    const set1 = new Set(arr1);
    return arr2.some((item) => !set1.has(item));
};

interface SyncIntegrationParams {
    jobId: number;
    platformName: string;
}

interface IntegrationsStatusContextProps {
    integrations: Integration[];
    inProgressIntegrations: string[];
    isLoading: boolean;
    syncIntegration: (data: SyncIntegrationParams, options?: MutationOptions) => void;
    isSyncing: boolean;
    refreshIntegrations: () => void;
}

const IntegrationsStatusContext = createContext<IntegrationsStatusContextProps | undefined>(undefined);

export const IntegrationsStatusProvider: React.FC<{ 
    children: ReactNode;
    isOnboarding?: boolean;
}> = ({ children, isOnboarding = false }) => {
    const queryClient = useQueryClient();
    const { currentCtUser } = useAuth();
    const integrationService = useIntegrationService();
    const previousInProgressRef = useRef<string[]>([]);

    const invalidateQueries = () => {
        QUERY_KEYS.dataQueriesWithTransactions().forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key] });
        });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REFRESH_BALANCE] });
    };

    // Main query to fetch integrations
    const { data: integrations, isFetching, refetch } = useQuery({
        queryKey: [QUERY_KEYS.INTEGRATIONS, isOnboarding],
        queryFn: async () => {
            // Use caching in onboarding mode to reduce unnecessary requests
            const loadedIntegrations = await integrationService.getIntegrationsOverview(!isOnboarding);
            const inProgressNames = loadedIntegrations
                .filter((integration) => integration.status.status === 'in_progress')
                .map((integration) => integration.name);
            // Invalidate relevant data if in-progress integrations change
            if (areArraysDifferent(previousInProgressRef.current, inProgressNames)) {
                QUERY_KEYS.dataQueriesWithTransactions().forEach((key) => {
                    queryClient.invalidateQueries({ queryKey: [key] });
                });
                queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RECALCULATION] });
            }

            previousInProgressRef.current = inProgressNames;
            return loadedIntegrations;
        },
        enabled: !!currentCtUser,
        placeholderData: [] as Integration[],
        // Disable automatic refetching in onboarding mode
        refetchOnWindowFocus: !isOnboarding,
        staleTime: isOnboarding ? Infinity : 0,
    });

    const inProgressIntegrations =
        integrations
            ?.filter((integration) => integration.status.status === 'in_progress')
            .map((integration) => integration.name) || [];

    // Sync Integration
    const { mutate: baseSyncIntegration, isPending: isSyncing } = useMutation({
        mutationKey: ['syncIntegration'],
        mutationFn: (params: SyncIntegrationParams) => integrationService.syncIntegration(params),
        onSuccess: () => {
            invalidateQueries();
        },
        onError: () => {},
    });

    const syncIntegration = (data: SyncIntegrationParams, options?: MutationOptions) => {
        baseSyncIntegration(data, {
            onSuccess: () => {
                options?.onSuccess?.();
            },
            onError: options?.onError,
        });
    };

    // Polling query to trigger refetch when integrations are in progress
    useQuery({
        queryKey: ['integrationsOverviewInterval', isOnboarding],
        queryFn: () => {
            // Only invalidate, the main query handles fetching
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
            return null;
        },
        refetchInterval: 5000, // Check every 5 seconds
        // Disable polling in onboarding mode
        enabled: !isOnboarding && inProgressIntegrations?.length > 0 && !isSyncing && !!currentCtUser,
    });

    // Function to manually refresh integrations (useful in onboarding)
    const refreshIntegrations = () => {
        refetch();
    };

    const value = {
        integrations: integrations || [],
        inProgressIntegrations,
        isLoading: isFetching,
        syncIntegration,
        isSyncing,
        refreshIntegrations,
    };

    return <IntegrationsStatusContext.Provider value={value}>{children}</IntegrationsStatusContext.Provider>;
};

export const useIntegrationsStatusContext = () => {
    const context = useContext(IntegrationsStatusContext);
    if (context === undefined) {
        throw new Error('useIntegrationsStatusContext must be used within an IntegrationsStatusProvider');
    }
    return context;
};
